import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridColumnMenuFilter,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnMenu } from '../components/columnMenu';
import YesNoDialog from '../components/dialogs/yes-no-dialog';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import {
  DeleteAssessmentAsync,
  DuplicateAssessmentAsync
} from '../services/assessment';
import { appStore } from '../stores/app-store';
import { YesNoDialogResult } from '../types/dialog-result';
import { AssessmentCardView } from './assessment-cardview';
import { CustomFilterLanguage } from './filter/custom-filter-language';
import { CardViewDataStateChangeEvent } from './organisations-cardview';
import { NoRecords } from '../components/grid/no-records';
import { ActionsMylibrary } from './command-grid/command-grid-mylibrary';
import { formatDateToDDMMYYYY } from '../utils/date';
import HelpSupport from '../components/help-support';
import { useToast } from '../contexts/toast-context';
import { useGetAssessments } from '../hooks/services/use-assessments';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

interface AssessmentGridProps {
  isApproval?: boolean;
}

const AssessmentGrid = ({ ...props }: AssessmentGridProps) => {
  const navigate = useNavigate();

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  const [selectedId, setSelectedId] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetAssessments(
    toDataSourceRequestString(dataState),
    props.isApproval ? 'admin' : undefined
  );

  const handleTitleClick = (id: number) => {
    if (props.isApproval) {
      navigate(`../assessment-detail-view/${id}`);
    } else {
      navigate(`../assessment-details/${id}`);
    }
  };

  const onColumnTitleClick = (props: GridCellProps) => (
    <EditCommandCell {...props} />
  );

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer p-0 text-start'}
          onClick={() => handleTitleClick(props.dataItem.id)}>
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  const onActionRow = (props: GridCellProps) => (
    <ActionsMylibrary
      onCopy={onCopyAssessment}
      onView={(e: any) => navigate(`../assessment-detail-view/${e as any}`)}
      onEdit={handleTitleClick}
      onDelete={onDelete}
      {...props}
    />
  );

  const StatusName = (props: GridCellProps) => (
    <td className={'k-table-td'}>
      {(() => {
        switch (props.dataItem.status) {
          case 'New':
            return 'Draft';
          case 'Published':
          case 'Rejected':
            return 'Published';
          case 'Pending':
            return 'Published/Submitted';
          case 'Approved':
            return 'Published/Global Library';
          default:
            return '{props.dataItem.status}';
        }
      })()}
    </td>
  );

  const onAddAssessment = () => {
    navigate('../mylibrary/assessment/new');
  };

  const onEditAssessment = (id: number) => {
    // navigate(`../assessment-details/${id}`);
  };

  const onCopyAssessment = async (id: number) => {
    try {
      appStore.showLoading();

      await DuplicateAssessmentAsync(id);

      onToastOpen({
        style: 'success',
        title: 'Assessment List',
        body: <div>Assessment copied to Assessment List.</div>
      });
    } catch (err: any) {
      onToastOpen({
        style: 'error',
        title: 'Assessment List',
        body: <div>Failed to copy to Assessment List.</div>
      });
    } finally {
      appStore.hideLoading();
      mutate();
    }
  };

  const onDelete = (id: number) => {
    onDeleteAssessment(id);
  };

  const onDeleteAssessment = async (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      try {
        appStore.showLoading();

        await DeleteAssessmentAsync(selectedId);
        mutate();

        onToastOpen({
          style: 'success',
          title: 'Assessment List',
          body: <div>Assessment deleted.</div>
        });
      } catch {
        onToastOpen({
          style: 'error',
          title: 'Assessment List',
          body: <div>Failed to delete assessment.</div>
        });
      } finally {
        appStore.hideLoading();
      }
    }
    setShowDeleteConfirm(false);
  };

  const closeDeleteDialog = () => {
    setShowDeleteConfirm(false);
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps, type: 'created' | 'updated') => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(
          props.dataItem[
            type === 'created' ? 'createdDateUtc' : 'modifiedDateUtc'
          ]
        )}
      </td>
    );
  };

  const CellEllipsisAndTooltip = (props: GridCellProps) => {
    return (
      <td title={props.dataItem.language} className={'k-table-td'}>
        <div className={'grid-ellipsis-cell'}>{props.dataItem.language}</div>
      </td>
    );
  };

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar
        title={`${
          props.isApproval ? 'Assessment Pending Approval' : 'Assessment'
        }`}>
        {!props.isApproval && (
          <>
            <HelpSupport
              title="My assessment library guide"
              url="https://forum.catalystglobal.com/t/4146"
            />
            <Button onClick={onAddAssessment} themeColor={'primary'}>
              Add new
            </Button>
          </>
        )}
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search assessment'}
          columnsToSearch={['name']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`assessment.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-assessment'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field={'name'}
                title={'Name'}
                columnMenu={ColumnMenu}
                cells={{
                  data: (props) => onColumnTitleClick(props)
                }}
              />
              <Column
                field={'createdDateUtc'}
                title={'Created'}
                width={110}
                cells={{
                  data: (props) => DateCell(props, 'created')
                }}
              />
              <Column
                field={'createdDateUtc'}
                title={'Updated'}
                width={110}
                cells={{
                  data: (props) => DateCell(props, 'updated')
                }}
              />
              <Column
                field={'source'}
                title={'Source'}
                filter={'text'}
                width={110}
                columnMenu={ColumnMenu}
              />
              <Column
                title={'Status'}
                field={'status'}
                width={180}
                cells={{
                  data: (props) => StatusName(props)
                }}
              />
              <Column
                field={'language'}
                title={'Languages'}
                width={200}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props)
                }}
                columnMenu={(props: any) => (
                  <GridColumnMenuFilter
                    {...props}
                    filterUI={CustomFilterLanguage}
                  />
                )}
              />
              {!props.isApproval && (
                <Column
                  title={'Actions'}
                  cells={{ data: (props) => onActionRow(props) }}
                  width={210}
                />
              )}

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}

          {gridMode === 'card' && (
            <AssessmentCardView
              data={data?.data ?? []}
              onTitlelick={handleTitleClick}
              onCopy={onCopyAssessment}
              onView={handleTitleClick}
              onEdit={onEditAssessment}
              onDelete={onDeleteAssessment}
              total={data?.total}
              {...dataState}
              onDataStateChange={(e: CardViewDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
            />
          )}
        </ExcelExport>

        {showDeleteConfirm && (
          <YesNoDialog
            title={'Remove Assessment'}
            onConfirm={onConfirmDelete}
            onClose={closeDeleteDialog}>
            Are you sure you want to delete this assessment?
          </YesNoDialog>
        )}
      </div>
    </>
  );
};
export default AssessmentGrid;
